import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { DataTableOptions } from '../model/data-table/data-table-options';
import { DataTableServerExport } from '../model/data-table/data-table-server-export';
import { StandardAction } from '../tool/action/standard-action';
import { GroupAction } from '../model/group-action';
import { StandardActionBuilder } from './action/standard-action-builder';

const logger = getInfCLogger('DataTableOptionsBuilder');

export class DataTableOptionsBuilder {
  private readonly _storageKey: string;
  private _paginationStep = 100;
  private _defaultSelection: number;
  private _selection = true;
  private _deselection = true;
  private _showRefresh = false;
  private _showTools = true;
  private _showPdf = false;
  private _showCsv = false;
  private _serverExport: DataTableServerExport;
  private _detailDisplay: 'none' | 'bottom' | 'right' = 'none';
  private _showDetailDisplay = false;
  private _showColumnSelector = true;
  private _showDetails = false;
  private _showRowHeight = false;
  private _rowHeight: 'S' | 'M' | 'L' = 'M';
  private _showFilter = false;
  private _showReset = false;
  private _showPagination = true;
  private _showTotal: boolean | 'switch' = false;
  private _showIndex = false;
  private _isTotalShown = false;
  private _multipleSelection = false;
  private _displayDetailOnSelection = false;
  private _showDelete = false;
  private _showEdit = false;
  private _openGroupsOnColumnGrouping = false;
  private _selectionStyle: 'marked' | 'none' = 'marked';
  private _enableViews = false;
  private _groupColumn = false;

  private _actionsToAdd: {
    groupId: string;
    actions: StandardAction[];
    position: 'start' | 'end';
  }[] = [];

  private _groupToAdd: {
    group: GroupAction;
    position: 'start' | 'end';
  }[] = [];

  constructor(storageKey: string) {
    this._storageKey = storageKey;
  }

  public setConfigDataTable(): DataTableOptionsBuilder {
    this._paginationStep = 50;
    this._defaultSelection = 0;
    this._selection = true;
    this._showTools = true;
    this._showRefresh = true;
    this._showPdf = true;
    this._showCsv = true;
    this._detailDisplay = 'none';
    this._showDetailDisplay = false;
    this._showColumnSelector = true;
    this._showDetails = false;
    this._showRowHeight = true;
    this._rowHeight = 'M';
    this._showFilter = true;
    this._showReset = true;
    this._showPagination = true;
    this._showTotal = false;
    this._isTotalShown = false;
    this._multipleSelection = false;
    this._openGroupsOnColumnGrouping = false;
    return this;
  }
  public setConfigMasterDetail(): DataTableOptionsBuilder {
    this.setConfigDataTable();
    this._deselection = true;
    this._showDetailDisplay = true;
    this._displayDetailOnSelection = true;
    this._detailDisplay = 'right';
    return this;
  }

  public setShowRefresh(value: boolean): DataTableOptionsBuilder {
    this._showRefresh = value;
    return this;
  }

  public setShowPdf(value: boolean): DataTableOptionsBuilder {
    this._showPdf = value;
    return this;
  }

  public setShowCsv(value: boolean): DataTableOptionsBuilder {
    this._showCsv = value;
    return this;
  }

  public setServerExport(value: DataTableServerExport): DataTableOptionsBuilder {
    this._serverExport = value;
    return this;
  }

  public setShowFilter(value: boolean): DataTableOptionsBuilder {
    this._showFilter = value;
    return this;
  }

  public setShowReset(value: boolean): DataTableOptionsBuilder {
    this._showReset = value;
    return this;
  }

  public setShowRowHeight(value: boolean): DataTableOptionsBuilder {
    this._showRowHeight = value;
    return this;
  }

  public setShowPagination(value: boolean): DataTableOptionsBuilder {
    this._showPagination = value;
    return this;
  }

  public setPaginationStep(value: number): DataTableOptionsBuilder {
    this._paginationStep = value;
    return this;
  }

  public setShowDetailDisplay(value: boolean): DataTableOptionsBuilder {
    this._showDetailDisplay = value;
    return this;
  }

  public setDetailDisplay(
    value: 'none' | 'bottom' | 'right',
  ): DataTableOptionsBuilder {
    this._detailDisplay = value;
    return this;
  }

  public setShowTotal(value: boolean | 'switch'): DataTableOptionsBuilder {
    this._showTotal = value;
    return this;
  }

  public setIsTotalShown(value: boolean): DataTableOptionsBuilder {
    this._isTotalShown = value;
    return this;
  }

  public setSelection(value: boolean): DataTableOptionsBuilder {
    this._selection = value;
    return this;
  }

  public setDeselection(value: boolean): DataTableOptionsBuilder {
    this._deselection = value;
    return this;
  }

  public setDefaultSelection(value: number): DataTableOptionsBuilder {
    this._defaultSelection = value;
    return this;
  }

  public setShowColumnSelector(value: boolean): DataTableOptionsBuilder {
    this._showColumnSelector = value;
    return this;
  }

  public setMultipleSelection(value: boolean): DataTableOptionsBuilder {
    this._multipleSelection = value;
    return this;
  }

  public setDisplayDetailOnSelection(value: boolean): DataTableOptionsBuilder {
    this._displayDetailOnSelection = value;
    return this;
  }

  public setRowHeight(value: 'S' | 'M' | 'L'): DataTableOptionsBuilder {
    this._rowHeight = value;
    return this;
  }

  public setShowDelete(value: boolean): DataTableOptionsBuilder {
    this._showDelete = value;
    return this;
  }

  public setShowEdit(value: boolean): DataTableOptionsBuilder {
    this._showEdit = value;
    return this;
  }

  public setGroupColumn(value: boolean): DataTableOptionsBuilder {
    this._groupColumn = value;
    return this;
  }

  public addActionsToGroup(
    groupId: string,
    action: StandardAction,
    position: 'start' | 'end',
  ): DataTableOptionsBuilder {
    this._actionsToAdd.push({
      groupId: groupId,
      actions: [action],
      position: position,
    });
    return this;
  }

  public addGroupAction(
    group: GroupAction,
    position: 'start' | 'end',
  ): DataTableOptionsBuilder {
    this._groupToAdd.push({ group: group, position: position });
    return this;
  }

  public addCreateAction(): DataTableOptionsBuilder {
    this.addActionsToGroup(
      'crud',
      new StandardActionBuilder('create', 'plus')
        .setTooltip('dataTable.tooltip.create')
        .build(),
      'start',
    );
    return this;
  }

  public setOpenGroupsOnColumnGrouping(
    value: boolean,
  ): DataTableOptionsBuilder {
    this._openGroupsOnColumnGrouping = value;
    return this;
  }

  public setShowIndex(value: boolean): DataTableOptionsBuilder {
    this._showIndex = value;
    return this;
  }

  public setSelectionStyle(value: 'marked' | 'none'): DataTableOptionsBuilder {
    this._selectionStyle = value;
    return this;
  }

  public setShowTools(value: boolean): DataTableOptionsBuilder {
    this._showTools = value;
    return this;
  }

  public enableViews(): DataTableOptionsBuilder {
    this._enableViews = true;
    return this;
  }

  public disableViews(): DataTableOptionsBuilder {
    this._enableViews = false;
    return this;
  }

  public build(): DataTableOptions {
    const options = new DataTableOptions();
    options.storageKey = this._storageKey;
    options.paginationStep = this._paginationStep;
    options.selection = this._selection;
    options.deselection = this._deselection;
    options.showRefresh = this._showRefresh;
    options.showTools = this._showTools;
    options.showPdf = this._showPdf;
    options.showCsv = this._showCsv;
    options.serverExport = this._serverExport;
    options.detailDisplay = this._detailDisplay;
    options.showDetailDisplay = this._showDetailDisplay;
    options.showColumnSelector = this._showColumnSelector;
    options.defaultSelection = this._defaultSelection;
    options.showDetails = this._showDetails;
    options.showRowHeight = this._showRowHeight;
    options.rowHeight = this._rowHeight;
    options.showFilter = this._showFilter;
    options.showReset = this._showReset;
    options.showPagination = this._showPagination;
    options.showTotal = this._showTotal;
    options.isTotalShown = this._isTotalShown;
    options.multipleSelection = this._multipleSelection;
    options.displayDetailOnSelection = this._displayDetailOnSelection;
    options.showDelete = this._showDelete;
    options.showEdit = this._showEdit;
    options.groupColumn = this._groupColumn;
    options.showIndex = this._showIndex;
    options.openGroupsOnColumnGrouping = this._openGroupsOnColumnGrouping;
    options.selectionStyle = this._selectionStyle;
    options.enableViews = this._enableViews;

    this._actionsToAdd.forEach((a) => {
      try {
        if (a.position === 'start') {
          options.findGroupById(a.groupId).actions.splice(0, 0, ...a.actions);
        }
        if (a.position === 'end') {
          options.findGroupById(a.groupId).actions.push(...a.actions);
        }
      } catch (e) {
        logger.error(`Impossible to find group "${a.groupId}"`, e);
      }
    });

    this._groupToAdd.forEach((g) => {
      switch (g.position) {
        case 'start':
          options.groupActions.unshift(g.group);
          break;
        case 'end':
          options.groupActions.push(g.group);
          break;
      }
    });
    return options;
  }
}
